import { Avatar, useTheme } from '@mui/material'

type CommentsAvatarProps = {
  /** Full name of the comment author, e.g. "Priya Nair". */
  name?: string | null
}

// Theme palette colors used to give each author a stable identity color.
// Semantic colors (primary/secondary/error/neutral/grey) are left out so the
// avatars read as identity, not status.
const AVATAR_COLORS = [
  'blue',
  'green',
  'orange',
  'teal',
  'purple',
  'indigo',
  'blueGrey',
  'pink',
  'deepPurple',
] as const

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Deterministic index from the name so a given author always gets the same
// color across renders and sessions.
function getColorIndex(name: string, length: number): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % length
}

export default function CommentsAvatar({ name }: CommentsAvatarProps) {
  const theme = useTheme()
  const displayName = name?.trim() || 'Unknown'
  const color =
    theme.palette[
      AVATAR_COLORS[getColorIndex(displayName, AVATAR_COLORS.length)]
    ]
  return (
    <Avatar
      sx={{
        width: 32,
        height: 32,
        fontSize: 13,
        fontWeight: 700,
        pt: '1px',
        bgcolor: color.main,
        color: color.contrastText,
      }}
    >
      {getInitials(displayName)}
    </Avatar>
  )
}
